import {BelegDTO, CreateBelegRequestDTO, UpdateBelegRequestDTO} from '../openapi/fakturierung-openapi';
import {DeepPartial} from '../types/deep-partial';
import {isAngebot} from './typeguards';


/**
 * Der PUT ist Vollersatz: Ein nicht uebergebenes Feld wird serverseitig geleert. Deshalb
 * bauen alle Aufrufer ihr Request-DTO ueber diese Funktion.
 *
 * Beim Angebot werden Leistungsdatum und Zahlungsziel nie gesendet, bei allen anderen
 * Belegarten nie die Gueltigkeit — auch nicht ueber die Overrides.
 */
export function createUpdateBelegRequest(
  beleg?: DeepPartial<BelegDTO>,
  overrides: Partial<UpdateBelegRequestDTO> = {},
): UpdateBelegRequestDTO {
  const request: UpdateBelegRequestDTO = {
    datum: beleg?.datum ?? '',
    leistungsdatum: beleg?.leistungsdatum || undefined,
    kundeId: beleg?.kundeId,
    kundeRevision: beleg?.kunde?.revision,
    vorlaufzeile: beleg?.vorlaufzeile,
    zahlungszielTage: beleg?.zahlungszielTage,
    gueltigkeitTage: beleg?.gueltigkeitTage,
    bestellnummer: beleg?.bestellnummer,
    lieferantennummer: beleg?.lieferantennummer,
    vertragsnummer: beleg?.vertragsnummer,
    projektnummer: beleg?.projektnummer,
    leitwegId: beleg?.leitwegId,
    ...overrides,
  };

  return isAngebot(beleg?.belegart)
    ? {...request, leistungsdatum: undefined, zahlungszielTage: undefined}
    : {...request, gueltigkeitTage: undefined};
}

/**
 * Overrides fuer die Uebernahme der Default-Frist aus den Einstellungen: Beim Angebot die
 * Gueltigkeit, sonst das Zahlungsziel.
 *
 * @param belegart Belegart des zu aktualisierenden Belegs.
 * @param zahlungszielTage Default-Zahlungsziel aus den Einstellungen, darf fehlen.
 * @param gueltigkeitTage Default-Gueltigkeit aus den Einstellungen, darf fehlen.
 * @returns Overrides fuer {@link createUpdateBelegRequest}.
 */
export function einstellungenFristOverrides(
  belegart: string | undefined,
  zahlungszielTage?: number,
  gueltigkeitTage?: number,
): Partial<UpdateBelegRequestDTO> {
  return isAngebot(belegart) ? {gueltigkeitTage} : {zahlungszielTage};
}

// INFO: toISOString() liefert das UTC-Datum, zwischen 0 und 2 Uhr Ortszeit also den Vortag.
function lokalesIsoDatum(datum: Date): string {
  const monat = String(datum.getMonth() + 1).padStart(2, '0');
  const tag = String(datum.getDate()).padStart(2, '0');
  return `${datum.getFullYear()}-${monat}-${tag}`;
}

/**
 * Baut den Create-Request fuer einen neuen Beleg mit heutigem Datum.
 *
 * Das Angebot traegt weder Leistungsdatum noch Zahlungsziel; die Gueltigkeit bleibt weg,
 * damit der Service den Default aus den Einstellungen zieht.
 *
 * @param belegart Belegart des neuen Belegs.
 * @param zahlungszielTage Default-Zahlungsziel, wird beim Angebot ignoriert.
 * @param heute Stichtag, Default ist das aktuelle Datum.
 * @returns Request-DTO der passenden `oneOf`-Variante.
 */
export function createCreateBelegRequest(
  belegart: string,
  zahlungszielTage?: number,
  heute: Date = new Date(),
): CreateBelegRequestDTO {
  const datum = lokalesIsoDatum(heute);

  if (isAngebot(belegart)) {
    return {belegart, datum};
  }

  // INFO: Keine Vorlaufzeile mitgeben → der Service setzt den sprachspezifischen Default.
  return {belegart, datum, leistungsdatum: datum, zahlungszielTage};
}
